import { useCompanionOnboardingStatus } from '@/hooks/use-companion-onboarding-status';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { Box, Button, HStack, Progress, Text, VStack } from '@chakra-ui/react';
import { SparkSolid } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const TOTAL_STEPS = 3;

/**
 * Discreet sidebar card prompting a brand to finish companion setup. Renders
 * only when setup is started but unfinished. Raised block on the dark menu (the rose spark
 * and progress are the highlight accent) with an explicit CTA button that routes
 * back into the wizard, which resolves the right resume step itself.
 */
export const CompanionResumeCard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: brand } = useCurrentBrandQuery();
  const { shouldShowResumeCard, stepsDone, resumeStep } = useCompanionOnboardingStatus(brand?.id);

  if (!shouldShowResumeCard) {
    return null;
  }

  return (
    <Box
      w="full"
      bg="navDark.surface"
      borderWidth="1px"
      borderColor="navDark.surfaceBorder"
      rounded="12px"
      p={3.5}
    >
      <VStack align="stretch" gap={2.5}>
        <HStack gap={1.5} align="center">
          <SparkSolid
            width={13}
            height={13}
            color="var(--chakra-colors-rose-300)"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          />
          <Text fontSize="sm" fontWeight="medium" color="softCream" lineClamp={2}>
            {t('companion.resume.title')}
          </Text>
        </HStack>

        <Progress.Root value={(stepsDone / TOTAL_STEPS) * 100} size="xs" colorPalette="rose">
          <Progress.Track bg="whiteAlpha.200">
            <Progress.Range />
          </Progress.Track>
        </Progress.Root>

        <Text fontSize="xs" color="whiteAlpha.700">
          {t('companion.resume.progress', { done: stepsDone })}
        </Text>

        <Button
          size="sm"
          bg="softCream"
          color="dark"
          _hover={{ bg: 'white' }}
          onClick={() => navigate(`/onboarding/companion?step=${resumeStep}`)}
          mt={0.5}
        >
          {t('companion.resume.cta')}
        </Button>
      </VStack>
    </Box>
  );
};
